import useAccountData from "hooks/useAccountData";
import { Company, ContactPoint, OpeningHours, OpeningHoursDay } from "models/Company";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import { companyService } from "services/Services";
import { ValidationSchema, DetailsValidationSchema, PhoneNumbersValidationSchema, LinksValidationSchema, IndustriesValidationSchema, OpeningHoursValidationSchema } from "models/ValidationSchema";
import { UrlPath } from "utility/routes";
import { AccountState } from "context/AccountContext";
import LoadingScreen from "components/LoadingScreen";
import _ from "lodash";

type Props = {
  children?: React.ReactChild | React.ReactChild[];
  validation: string | undefined;
};

export default function FormContainer({ children, validation }: Props) {
  let params = useParams();
  const navigate = useNavigate();
  const {loadingCompanies, getCurrentCompany} = useAccountData();
  const [input, setInput] = useState<Company | null>(null);
  const [isSubmittingForm, setSubmittingForm] = useState(false);
  const { stateAccount } = AccountState();

  const updateCompanyData = async (response: Company) => {
    const getUpdatedData = await companyService.updateCompany(response);
    if(getUpdatedData?.success && getUpdatedData?.company) {

      if (getUpdatedData.company.id !== response.id){
        stateAccount.addCompany(getUpdatedData.company);
        stateAccount.removeCompany(response);
      }
      else{
      stateAccount.updateCompany(getUpdatedData?.company);
      }
      return getUpdatedData.company.id;
    }
    return response.id;
  };

  const validationSchema = () => {
    switch(validation){
      case(UrlPath.Account.Actions.Details):
        return DetailsValidationSchema;
      case(UrlPath.Account.Actions.PhoneNumbers):
        return PhoneNumbersValidationSchema;
      case(UrlPath.Account.Actions.Links):
        return LinksValidationSchema;
      case(UrlPath.Account.Actions.Industries):
        return IndustriesValidationSchema;
      case(UrlPath.Account.Actions.OpeningHours):
        return OpeningHoursValidationSchema;
      default:
        return ValidationSchema;
    }
  }

  const onSubmit = async (values: Company) => {
    setSubmittingForm(true);
    const id = await updateCompanyData(values);
    setSubmittingForm(false);
    navigate(UrlPath.Account.Company + "/" + id) // Alterative 1
    //navigate(UrlPath.MyAccount.Home, { state: { reloadCompanies: true } }); // Alternative 2
  };

  const isDirty = (values: Company, initialValues: Company): boolean => {
    const omitUpdated = (contactPoints: ContactPoint[]) =>
      contactPoints.map(cp => _.omit(cp, ['updated']));
  
    const filteredValues = {
      ..._.omit(values,['openingHours']),
      contactPoints: omitUpdated(values.contactPoints),
      emails: omitUpdated(values.emails),
      urls: omitUpdated(values.urls),
      phoneNumbers: omitUpdated(values.phoneNumbers),
      socialMediaUrls: omitUpdated(values.socialMediaUrls),
    };
  
    const filteredInitialValues = {
      ..._.omit(initialValues, 'openingHours'),
      contactPoints: omitUpdated(initialValues.contactPoints),
      emails: omitUpdated(initialValues.emails),
      urls: omitUpdated(initialValues.urls),
      phoneNumbers: omitUpdated(initialValues.phoneNumbers),
      socialMediaUrls: omitUpdated(initialValues.socialMediaUrls),
    };

    let ophEqual = ophIsEqual(initialValues, values);

    return _.isEqual(filteredValues, filteredInitialValues) && ophEqual;
  };

  const ophIsEqual = (initialValues: Company, values: Company) : boolean => {
    const isEmptyObject = (obj:Object) => 
      Object.keys(obj).length === 0 && obj.constructor === Object;

    const filterNonEmpty = (list: OpeningHoursDay[] | undefined) => {
      if (list === undefined || list === null) return [];
      return list.filter((obj: OpeningHoursDay) => !isEmptyObject(obj));
    };

    const areListsEqual = (listA:Object[] | undefined, listB:Object[] | undefined) => {
      if (listA === undefined || listB === undefined)
        return false;
      if (listA.length !== listB.length) return false;
      return listA.every((obj, index) => 
        JSON.stringify(obj) === JSON.stringify(listB[index])
      );
    };

    let filteredInitalDays = filterNonEmpty(initialValues.openingHours?.days);
    let filteredValuesDays = filterNonEmpty(values.openingHours?.days);
    
    const sortedFilteredList1 = filteredInitalDays?.sort((a:OpeningHoursDay, b:OpeningHoursDay) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    const sortedFilteredList2 = filteredValuesDays?.sort((a:OpeningHoursDay, b:OpeningHoursDay) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    
    const showOp = (op: OpeningHours | undefined) => { 
      if(op === undefined || op === null || op.showOpeningHours == null)
      return false; return op.showOpeningHours}

      let ophIsEqual = (
        (showOp(values.openingHours) === showOp(initialValues.openingHours) && showOp(values.openingHours) === false)
          ? showOp(values.openingHours) === showOp(initialValues.openingHours)
          : showOp(values.openingHours) === showOp(initialValues.openingHours) && areListsEqual(sortedFilteredList1, sortedFilteredList2)
      );

    return ophIsEqual;
  }


  useEffect(() => {
    async function fetchData() {
      if(loadingCompanies) {
        const response = await getCurrentCompany(params.id as string);
        setInput(response);
      }
    }
    fetchData();
  }, [getCurrentCompany, params.id, loadingCompanies]);

  return (
    <>
    {input === null ? (<LoadingScreen />) : (
      <>
        <Formik
          initialValues={input}
          onSubmit={onSubmit}
          enableReinitialize
          validationSchema={validationSchema}
        >
          {({ errors, touched, dirty,values, initialValues }) => (
          <Form>
            {children}
            <p className="mt-4 mb-0">
              <button type="submit" className={"btn btn-primary text-white me-1 "} disabled={isSubmittingForm || isDirty(values,initialValues)}>
                Lagre endringer {isSubmittingForm ? (<span className="spinner-border spinner-border-sm"></span>) : null}
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => navigate(UrlPath.Account.Company + "/" + params.id)} // Alternative 1
                disabled={isSubmittingForm}
                //onClick={() => navigate(UrlPath.MyAccount.Home)} // Alternative 2
              >
                Avbryt
              </button>
            </p>
          </Form>
          )}
        </Formik>
      </>)}
    </>
  );
};